import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { ReactNode, useCallback, useMemo } from 'react'

import { ITableColumn } from 'partner360/components/financialTable'
import { FinancialTable } from 'partner360/components/financialTable'
import { IChartSettings } from 'partner360/components/QBR/shared/QBRBarChart/models'
import { changeIsSelectedDocumentChanged } from 'partner360/features/QBR'

import { SelectedPeriodWithPercent } from '../selectedPeriodWithPercent/SelectedPeriodWithPercent'

import { QbrChartsGroup } from './components/qbrChartsGroup/QbrChartsGroup'
import { TABLE_HIERARCHY_FIELD, TABLE_KEY_FIELD } from './configs'
import { buildHierarchyGroups } from './helpers/buildHierarchyGroups'

interface IFinancialDataVisualizer<T> {
  columns: ITableColumn<T>[]
  currencyLabel?: string
  chartLabelNode?: ReactNode
  columnsForCharts: ITableColumn<T>[]
  data: T[]
  className?: string
  chartNameField: string
  getBarsSettings: (item: T) => IChartSettings[]
  getLinesSettings: (item: T) => IChartSettings[]
  expandedNodes: T[]
  showUpToDateColumn: boolean
  selectedPeriodFYear: number
  selectedPeriodFQuarters: number[]
  onExpand: (keyField: string, keyFieldValue: string | number, hierarchyField: string) => void
  onCollapse: (keyField: string, keyFieldValue: string | number, hierarchyField: string) => void
  isSob?: boolean
}

export const FinancialDataVisualizer = <T extends { ParentId: number | null; EntryId: number }>({
  data,
  currencyLabel = '',
  chartLabelNode,
  className,
  columns,
  chartNameField,
  getBarsSettings,
  getLinesSettings,
  onExpand,
  onCollapse,
  expandedNodes,
  columnsForCharts,
  showUpToDateColumn,
  selectedPeriodFYear,
  selectedPeriodFQuarters,
  isSob,
}: IFinancialDataVisualizer<T>) => {
  const dispatch = useAppDispatch()

  const headerGroups = useMemo(() => {
    const previousFYear = selectedPeriodFYear - 1
    let previousYearColumns = 5

    if (showUpToDateColumn) {
      previousYearColumns = previousYearColumns + 1
    }

    return [
      { name: currencyLabel, colspan: 2, withoutBorder: true },
      { name: previousFYear.toString(), colspan: previousYearColumns },
      { name: selectedPeriodFYear.toString(), colspan: selectedPeriodFQuarters.length + 1 },
    ]
  }, [currencyLabel, selectedPeriodFYear, selectedPeriodFQuarters])

  const expandedKeys = useMemo(() => {
    return expandedNodes?.map((el) => el[TABLE_KEY_FIELD])
  }, [expandedNodes])

  const visibleItems = useMemo(() => {
    const alwaysVisibleValues = data.filter((el: T) => el[TABLE_HIERARCHY_FIELD] === null)

    const visibleItems = expandedKeys.reduce((acc, key) => {
      const found = data.find((el) => el[TABLE_KEY_FIELD] === key)
      if (found) {
        const children = data.filter((el) => el[TABLE_HIERARCHY_FIELD] === key)

        return [...acc, found, ...children]
      }

      return []
    }, [] as T[])

    return [...alwaysVisibleValues, ...visibleItems].filter(
      (obj, index, self) =>
        index === self.findIndex((o) => o[TABLE_KEY_FIELD] === obj[TABLE_KEY_FIELD])
    )
  }, [data, expandedKeys])

  const grouped = buildHierarchyGroups<T>({ items: visibleItems })

  const onCollapseRow = useCallback(
    (row: T) => {
      onCollapse(TABLE_KEY_FIELD, row[TABLE_KEY_FIELD], TABLE_HIERARCHY_FIELD)
      dispatch(changeIsSelectedDocumentChanged(true))
    },
    [onCollapse, dispatch]
  )

  const onExpandRow = useCallback(
    (el: T) => {
      onExpand(TABLE_KEY_FIELD, el[TABLE_KEY_FIELD], TABLE_HIERARCHY_FIELD)
      dispatch(changeIsSelectedDocumentChanged(true))
    },
    [onExpand, dispatch]
  )

  return (
    <div>
      <SelectedPeriodWithPercent />
      <FinancialTable<T>
        className={className}
        hierarchyField={TABLE_HIERARCHY_FIELD}
        keyField={TABLE_KEY_FIELD}
        columns={columns}
        data={data}
        headerGroups={headerGroups}
        expandedKeys={expandedKeys}
        onExpand={onExpandRow}
        onCollapse={onCollapseRow}
        withHierarchy
      />

      <div>
        {grouped.map((item) => {
          return (
            <div key={item[TABLE_KEY_FIELD]}>
              <QbrChartsGroup
                item={item}
                selectedPeriodFYear={selectedPeriodFYear}
                selectedPeriodFQuarters={selectedPeriodFQuarters}
                chartLabelNode={chartLabelNode}
                chartNameField={chartNameField}
                getBarsSettings={getBarsSettings}
                getLinesSettings={getLinesSettings}
                columns={columnsForCharts}
                isSob={isSob}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
